import { layoutDefaults, useColumns } from "./DocumentsDataTableColumns";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { useEffect, useState } from "react";
import { EditDocumentSheet } from "./EditDocumentSheet";
import { DataTable } from "@repo/ui/components/Base/DataTable";
import type { Attachment, Document } from "@repo/types";
import { ArchiveConfirmation } from "../dialog/ArchiveConfirmation";
import { DocumentPreviewer } from "../dialog/DocumentPreviewer";
import { useUserColumns } from "@/hooks/useUserColumns";
import { KEYS } from "@/lib/constants";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { useNavigate } from "react-router-dom";
import { downloadFile, openInNewTab } from "@repo/shared/utils";
import { apiService } from "@/services/apiService";
import { documentRepo } from "@/repositories/documentRepo";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";

export const DocumentsTable = ({ tenantId }: { tenantId: string }) => {
  const navigate = useNavigate();
  const { data: documents } = useDocumentList(
    tenantId,
    useActiveTeamIdOrOrg() ?? ""
  );
  const [activeDocument, setActiveDocument] = useState<string>();
  const [activeAttachment, setActiveAttachment] = useState<Attachment>();
  const [activeDocumentUrl, setActiveDocumentUrl] = useState<string>();
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.DOCUMENT_TABLE_COLUMNS,
    layoutDefaults
  );

  const getDocumentUrl = async (doc: Document | null | undefined) => {
    if (!doc?.confidential) {
      const attachment = doc ? documentRepo.getLinkedAttachment(doc) : null;
      return attachment?.url ?? "javascript:void(0)";
    }

    const { data: url } = await apiService.v1.documentDownloadUrl(
      tenantId,
      doc.id
    );

    return url ?? "javascript:void(0)";
  };

  const handleEdit = (docId: string) => {
    setEditSheetOpen(true);
    setActiveDocument(docId);
  };

  const handleArchive = (docId: string) => {
    setActiveDocument(docId);
    setArchiveOpen(true);
  };

  const handleDownload = async (doc: Document) => {
    const attachment = documentRepo.getLinkedAttachment(doc);
    if (attachment?.isUrl) {
      openInNewTab(attachment.url);
    } else {
      downloadFile(await getDocumentUrl(doc), attachment?.name);
    }
  };

  const handleView = (id: string) => {
    navigate(new AppUrlPath(tenantId).getDocumentPath(`${id}`));
  };

  useEffect(() => {
    const doc = documents?.find((d) => d.id === activeDocument);
    if (doc) {
      const attachment = documentRepo.getLinkedAttachment(doc);
      setActiveAttachment(attachment ?? undefined);
    }
  }, [activeDocument, documents]);

  const handlePreview = async (docId: string) => {
    const doc = documents?.find((doc) => doc.id === docId);
    const attachment = doc ? documentRepo.getLinkedAttachment(doc) : null;
    if (attachment?.url && attachment.isUrl) {
      openInNewTab(attachment.url);
      return;
    }

    const url = await getDocumentUrl(doc);

    setActiveDocument(docId);
    setActiveDocumentUrl(url);
    setDocumentPreviewOpen(true);
  };

  return (
    <>
      {activeAttachment && activeDocumentUrl && (
        <DocumentPreviewer
          title={activeAttachment.name}
          open={documentPreviewOpen}
          setOpen={setDocumentPreviewOpen}
          documentUri={activeDocumentUrl}
        />
      )}
      {activeDocument && (
        <ArchiveConfirmation
          open={archiveOpen}
          setOpen={setArchiveOpen}
          itemId={activeDocument}
          onSubmit={async (documentId) => {
            await documentRepo.delete(tenantId, documentId);
          }}
        />
      )}
      {!documents && <Spinner />}
      {activeDocument && (
        <EditDocumentSheet
          open={editSheetOpen}
          setOpen={setEditSheetOpen}
          tenantId={tenantId}
          documentId={activeDocument}
        />
      )}

      <DataTable
        columns={useColumns({
          tenantId,
          handleEdit,
          handleArchive,
          handlePreview,
          handleDownload,
          handleView,
        })}
        data={documents ?? []}
        includeMainFilter={false}
        userDefaults={defaultState}
        factoryDefaults={layoutDefaults}
        onLayoutSave={saveTableLayout}
        onLayoutReset={resetTableLayout}
      />
    </>
  );
};
